/**
 * useChatbotConfig Hook
 *
 * High-performance, memoized hook for components that only need chatbot configuration data.
 * Reads pre-rendered data from dataset attributes (populated by footer.jsp).
 *
 * Use cases:
 * - Avatar components
 * - Thread components
 * - UI components that need config for styling/behavior
 *
 * Benefits:
 * - Zero API calls - uses server-side pre-rendered data
 * - Instant availability on page load
 * - Stable references prevent unnecessary re-renders
 * - Graceful fallbacks to defaults
 */

import { useMemo } from 'react';
import { DEFAULT_CHATBOT_COLOR, DEFAULT_COLOR_OVER_THEME } from '../constants';
import type { ChatbotConfig } from '../types/configuration';
import type {
  ConfigurationResponse,
  CmsThemeColors,
} from '../types/configuration';
import type {
  CustomTypesResponse,
  CmsThemeColorEnum,
} from '../types/customTypes';

export interface UseChatbotConfigReturn {
  /** Chatbot configuration object - null if not loaded/configured */
  config: ChatbotConfig | null;

  /** Chatbot color (hex string) with fallback to default */
  color: string;

  /** Color to display over the theme background (hex string) */
  colorOverTheme: string;

  /** Whether chatbot is properly configured */
  isConfigured: boolean;

  /** Configuration error if any */
  error: unknown;
}

/**
 * Hook for accessing chatbot configuration with optimizations for rendering components.
 * Data is read from pre-rendered HTML dataset attributes.
 *
 * @example
 * ```tsx
 * const { config, color, isConfigured } = useChatbotConfig();
 *
 * if (!isConfigured) return null;
 *
 * return <ChatbotAvatar config={config} color={color} />;
 * ```
 */
export const useChatbotConfig = (): UseChatbotConfigReturn => {
  return useMemo(() => {
    try {
      // Read pre-rendered data from dataset attributes
      const rootElement = document.getElementById('react-app-root');
      const configDatasetValue = rootElement?.dataset?.chatbotConfiguration;
      const customTypesDatasetValue = rootElement?.dataset?.chatbotCustomTypes;

      if (!configDatasetValue || !customTypesDatasetValue) {
        return {
          config: null,
          color: DEFAULT_CHATBOT_COLOR,
          colorOverTheme: DEFAULT_COLOR_OVER_THEME,
          isConfigured: false,
          error: null,
        };
      }

      // Parse the JSON data
      const configurationData = JSON.parse(
        configDatasetValue,
      ) as ConfigurationResponse;
      const customTypesData = JSON.parse(
        customTypesDatasetValue,
      ) as CustomTypesResponse;

      const rawConfig = configurationData?.payload?.clientSettings || null;
      const cmsThemeColors = configurationData?.payload?.cmsThemeColors || null;
      const colorEnum =
        customTypesData?.payload?.enums?.CmsThemeColorEnum || null;

      if (!rawConfig || !colorEnum) {
        return {
          config: null,
          color: DEFAULT_CHATBOT_COLOR,
          colorOverTheme: DEFAULT_COLOR_OVER_THEME,
          isConfigured: false,
          error: null,
        };
      }

      // Resolve the color using the CMS colors and enum mapping
      const resolvedColor = resolveColorFromEnum(
        rawConfig.color,
        cmsThemeColors,
        colorEnum,
      );

      // Resolve the color over theme (foreground color for icons/text)
      const resolvedColorOver = resolveColorOverFromEnum(
        rawConfig.color,
        cmsThemeColors,
        colorEnum,
      );

      // Build the complete chatbot config with resolved color
      const config: ChatbotConfig = {
        ...rawConfig,
        colorHex: resolvedColor,
        iconUrl: null, // Still a stub field until microservice provides it
      };

      return {
        config,
        color: resolvedColor,
        colorOverTheme: resolvedColorOver,
        isConfigured: true,
        error: null,
      };
    } catch (error) {
      console.error(
        'Failed to parse chatbot configuration from dataset:',
        error,
      );
      return {
        config: null,
        color: DEFAULT_CHATBOT_COLOR,
        colorOverTheme: DEFAULT_COLOR_OVER_THEME,
        isConfigured: false,
        error,
      };
    }
  }, []); // Empty deps - data is static from server-side rendering
};

/**
 * Resolves the hex color value from CMS colors based on the enum value.
 * Uses the CmsThemeColorEnum from custom types to dynamically map enum values to color names.
 *
 * @param selectedColorValue - The selected color enum value (e.g., 1 or 2)
 * @param cmsThemeColors - The CMS colors object with primaryColor and secondaryColor hex values
 * @param colorEnum - The CmsThemeColorEnum mapping from custom types API
 * @returns The resolved hex color string
 */
function resolveColorFromEnum(
  selectedColorValue: number,
  cmsThemeColors: CmsThemeColors | null,
  colorEnum: CmsThemeColorEnum,
): string {
  if (!cmsThemeColors) {
    return DEFAULT_CHATBOT_COLOR;
  }

  // Find the enum name that matches the selected value
  // e.g., if selectedColorValue is 1, find "PrimaryColor"
  const colorName = Object.keys(colorEnum).find(
    (key) => colorEnum[key as keyof CmsThemeColorEnum] === selectedColorValue,
  );

  if (!colorName) {
    console.warn(
      `Unknown color enum value: ${selectedColorValue}, falling back to default`,
    );
    return DEFAULT_CHATBOT_COLOR;
  }

  // Map the enum name directly to the corresponding CMS color property
  // "PrimaryColor" -> cmsThemeColors.PrimaryColor
  // "SecondaryColor" -> cmsThemeColors.SecondaryColor
  const colorValue = cmsThemeColors[colorName as keyof CmsThemeColors];

  if (colorValue) {
    return colorValue;
  }

  console.warn(
    `Unknown color name: ${colorName}, falling back to PrimaryColor`,
  );
  return cmsThemeColors.PrimaryColor || DEFAULT_CHATBOT_COLOR;
}

/**
 * Resolves the ColorOver* hex color value to display over the theme background.
 * Maps the enum value to the corresponding ColorOverPrimary or ColorOverSecondary.
 *
 * @param selectedColorValue - The selected color enum value (e.g., 1 or 2)
 * @param cmsThemeColors - The CMS colors object with ColorOver* values
 * @param colorEnum - The CmsThemeColorEnum mapping from custom types API
 * @returns The resolved hex color string for text/icons over the theme
 */
function resolveColorOverFromEnum(
  selectedColorValue: number,
  cmsThemeColors: CmsThemeColors | null,
  colorEnum: CmsThemeColorEnum,
): string {
  if (!cmsThemeColors) {
    return DEFAULT_COLOR_OVER_THEME;
  }

  // Find the enum name that matches the selected value
  // e.g., if selectedColorValue is 1, find "PrimaryColor"
  const colorName = Object.keys(colorEnum).find(
    (key) => colorEnum[key as keyof CmsThemeColorEnum] === selectedColorValue,
  );

  if (!colorName) {
    console.warn(
      `Unknown color enum value: ${selectedColorValue}, falling back to ColorOverPrimary`,
    );
    return cmsThemeColors.ColorOverPrimary || DEFAULT_COLOR_OVER_THEME;
  }

  // Map the enum name to the corresponding ColorOver property
  // "PrimaryColor" -> cmsThemeColors.ColorOverPrimary
  // "SecondaryColor" -> cmsThemeColors.ColorOverSecondary
  if (colorName === 'PrimaryColor') {
    return cmsThemeColors.ColorOverPrimary || DEFAULT_COLOR_OVER_THEME;
  } else if (colorName === 'SecondaryColor') {
    return cmsThemeColors.ColorOverSecondary || DEFAULT_COLOR_OVER_THEME;
  }

  console.warn(
    `Unknown color name: ${colorName}, falling back to ColorOverPrimary`,
  );
  return cmsThemeColors.ColorOverPrimary || DEFAULT_COLOR_OVER_THEME;
}
